import { CircleFadingPlus, FilePlus, X } from "lucide-react";
import { FC, useMemo, useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { useListDocuments } from "~/api/default/default";
import { useGetVolumes } from "~/api/folders/folders";
import { DocumentStatus } from "~/api/model";
import { Button } from "~/components/ui/button";
import { useProjectParams } from "~/helpers";
import { cn } from "~/lib/utils";
import { useDocumentSelectionStore } from "../../stores/documentSelectionStore";
import { AddToVersionModal } from "../AddToVersionDialog";
import { CreateVersionModal } from "../CreateVersionDialog";

export const DocumentsPanel: FC = () => {
  const {
    getSelectedCount,
    isSelectionMode,
    clearSelection,
    selectedDocumentIds,
  } = useDocumentSelectionStore();
  const [open, setOpen] = useState(false);
  const { formatMessage } = useIntl();
  const [openAddToVersionModal, setOpenAddToVersionModal] = useState(false);
  const { organizationId, caseId } = useProjectParams();
  const { data: documentsList } = useListDocuments(organizationId, caseId);
  const { data: volumes } = useGetVolumes(organizationId, caseId);

  // Get count of complete documents
  const completeDocumentsCount = documentsList?.documents
    ? selectedDocumentIds.filter((id) => {
        const document = documentsList.documents?.find((doc) => doc.id === id);
        return document?.status === DocumentStatus.COMPLETE;
      }).length
    : 0;

  const totalDocuments = documentsList?.documents?.length || 0;

  const allDocumentsSelected =
    getSelectedCount() === totalDocuments && totalDocuments > 0;

  const hasVersions = useMemo(() => {
    if (!volumes) return false;

    // If we are in the master, show button if there is at least one version
    if (volumes.some((v) => v.id === caseId && v.parentId === null)) {
      return volumes.some((volume) => volume.parentId !== null);
    }

    // If we are in the version, show button if there is at least one other version
    return (
      volumes.filter(
        (volume) => volume.parentId !== null && volume.id !== caseId
      ).length > 0
    );
  }, [volumes, caseId]);

  const numberOfFilesSelected = getSelectedCount();

  return (
    <>
      <div
        className={cn(
          "fixed flex gap-2 bottom-0 left-1/2 -translate-x-1/2 h-auto p-[8px] pl-[16px] rounded-xl shadow-lg shadow-black/10 justify-center items-center mb-4 flex-col z-10",
          {
            "bg-background w-auto text-muted-foreground pointer-events-none":
              !isSelectionMode,
            "bg-blue-600 text-white w-auto pointer-events-none":
              isSelectionMode,
          }
        )}
      >
        <div className="flex gap-2 items-center">
          <span className="text-sm font-medium leading-tight inline-block ml-[8px] mr-[12px]">
            {numberOfFilesSelected}{" "}
            {formatMessage({
              id: numberOfFilesSelected > 1 ? "filesSelected" : "fileSelected",
            })}
            {completeDocumentsCount < numberOfFilesSelected &&
              ` (${completeDocumentsCount} ${formatMessage(
                { id: "processable" },
                { count: completeDocumentsCount }
              )})`}
          </span>
          {hasVersions && !allDocumentsSelected && (
            <Button
              className="bg-blue-600 text-white pointer-events-auto border border-blue-500"
              onClick={() => setOpenAddToVersionModal(true)}
              disabled={completeDocumentsCount === 0}
            >
              <FilePlus className="w-4 h-4 mr-2" />
              <FormattedMessage
                id="addToVersion"
                defaultMessage="Add to Version"
              />
            </Button>
          )}
          <Button
            className="bg-white text-blue-600 pointer-events-auto hover:bg-blue-100 border border-blue-600"
            onClick={() => setOpen(true)}
            disabled={completeDocumentsCount === 0}
          >
            <CircleFadingPlus className="w-4 h-4 mr-2" />
            <FormattedMessage
              id="createNewVersion"
              defaultMessage="Create New Version"
            />
          </Button>
          <Button
            variant="outline"
            size="icon"
            className="bg-blue-600 hover:bg-blue-700 border-transparent !text-white pointer-events-auto border-none"
            onClick={clearSelection}
          >
            <X className="w-4 h-4" />
          </Button>
        </div>
        {completeDocumentsCount < getSelectedCount() && (
          <p className="text-xs text-white opacity-80 pb-1">
            Note: Only processed files will be included in the new version
          </p>
        )}
      </div>

      <CreateVersionModal open={open} setOpen={setOpen} />
      <AddToVersionModal
        open={openAddToVersionModal}
        setOpen={setOpenAddToVersionModal}
      />
    </>
  );
};
